/**
 * React Query hook for Practice Areas
 * Provides automatic caching, background sync, and optimistic updates
 */

import { useQuery } from '@tanstack/react-query';
import { getPracticeAreas } from 'services/lead/leadServices';
import { totalCountEvent } from 'services/generalServices';
import { queryKeys } from '../../config/queryClient';

/**
 * Fetches all practice areas
 *
 * @param {Object} options - Query options
 * @param {boolean} options.loadFullList - If true, fetches all practice areas, otherwise uses pagination
 * @param {number} options.pageSize - Number of items per page (default: 10)
 * @param {string} options.status - Filter by status
 * @param {boolean} options.excludeFromLoading - Exclude from global loading state
 * @returns {Promise<Array>} - Array of practice areas
 */
const fetchPracticeAreas = async ({ loadFullList = true, pageSize = 10, status, excludeFromLoading = false }) => {
  let pageLimit = pageSize;

  if (loadFullList) {
    // Get total count first
    const countResponse = await totalCountEvent('practice-area', '', '', '', '', excludeFromLoading);
    pageLimit = countResponse.data;
  }

  // Fetch practice areas
  const response = await getPracticeAreas({
    pageLimit,
    pagination_token: '',
    pagination_direction: 'forward',
    status,
  });

  return response.data?.['practice-area'] || [];
};

/**
 * Hook to fetch practice areas with React Query
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.loadFullList - If true, loads all practice areas (default: true)
 * @param {number} options.pageSize - Number of items per page (default: 10)
 * @param {string} options.status - Filter by status
 * @param {boolean} options.excludeFromLoading - Exclude from global loading state (default: false)
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 *
 * @returns {Object} - practiceAreaList, isPracticeAreaLoading, error, refetch
 */
export const usePracticeAreaQuery = ({ loadFullList = true, pageSize = 10, status, excludeFromLoading = false, enabled = true } = {}) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.practiceAreas.list(),
    queryFn: () => fetchPracticeAreas({ loadFullList, pageSize, status, excludeFromLoading }),
    staleTime: 10 * 60 * 1000, // Consider data stale after 5 minutes
    cacheTime: 30 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus for relatively static data
    retry: 2,
    enabled, // Allow disabling the query
  });

  return {
    practiceAreaList: data || [],
    isPracticeAreaLoading: isLoading,
    error,
    refetch,
  };
};
